import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Lock, Plus } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { FormProblem } from '../components/FormProblem';
import { PageHeader } from '../components/PageHeader';
import { useToast } from '../components/Toast';
import { useAuth } from '../features/auth/AuthProvider';
import {
  fieldMessages,
  kindLabels,
  listPermissions,
  listRoles,
  permissionsKey,
  rolesKey,
  updateRole,
  type PermissionInfo,
  type Role,
} from '../features/users/api';
import { ProfileDrawer } from '../features/users/ProfileDrawer';
import { ApiError } from '../lib/api';

type Draft = Record<string, string[]>;

/** Rows of the table grouped by screen, in catalog order. */
function byArea(perms: PermissionInfo[]) {
  const out: { area: string; items: PermissionInfo[] }[] = [];
  for (const p of perms) {
    const last = out.at(-1);
    if (last?.area === p.area) last.items.push(p);
    else out.push({ area: p.area, items: [p] });
  }
  return out;
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((k) => b.includes(k));

/**
 * The permission table (ADR-027): every screen and feature against every
 * profile; the administrator ticks what each profile may do.
 */
export function RolesPage() {
  const { request, can } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const canManage = can('roles.manage');
  const roles = useQuery({ queryKey: rolesKey, queryFn: () => listRoles(request) });
  const perms = useQuery({ queryKey: permissionsKey, queryFn: () => listPermissions(request) });
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<{ role?: Role } | null>(null);

  const areas = useMemo(() => byArea(perms.data ?? []), [perms.data]);
  const list = roles.data ?? [];
  const current = (r: Role) => draft[r.id] ?? r.permissions;
  const changed = list.filter((r) => draft[r.id] && !sameSet(draft[r.id] ?? [], r.permissions));

  const toggle = (r: Role, key: string, on: boolean) => {
    const now = current(r);
    setDraft({ ...draft, [r.id]: on ? [...now, key] : now.filter((k) => k !== key) });
  };

  const save = async () => {
    setSaving(true);
    setProblem(null);
    const failed: string[] = [];
    for (const r of changed) {
      try {
        await updateRole(request, r.id, { permissions: draft[r.id] ?? r.permissions });
      } catch (err) {
        const why =
          err instanceof ApiError && err.status === 422
            ? Object.values(fieldMessages(err.fields)).join(' ')
            : 'não foi possível salvar.';
        failed.push(`${r.name}: ${why}`);
      }
    }
    await qc.invalidateQueries({ queryKey: rolesKey });
    setSaving(false);
    if (failed.length > 0) {
      setProblem(failed.join(' '));
      return;
    }
    setDraft({});
    notify(
      changed.length === 1
        ? `Permissões de ${changed[0]?.name ?? ''} salvas.`
        : `Permissões de ${String(changed.length)} perfis salvas.`,
    );
  };

  const failed = roles.isError || perms.isError;
  return (
    <>
      <PageHeader
        title="Perfis e permissões"
        description="O que cada perfil pode fazer em cada tela. As mudanças valem na hora para todos os usuários do perfil."
        actions={
          canManage && (
            <Button
              variant="primary"
              disabled={!roles.data}
              onClick={() => {
                setDrawer({});
              }}
            >
              <Plus aria-hidden="true" className="size-4" strokeWidth={2} />
              Novo perfil
            </Button>
          )
        }
      />
      <div className="space-y-3 p-4 sm:p-6">
        <FormProblem message={problem} />
        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {failed ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar os perfis"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button
                  variant="primary"
                  onClick={() => {
                    void roles.refetch();
                    void perms.refetch();
                  }}
                >
                  Tentar novamente
                </Button>
              }
            />
          ) : !roles.data || !perms.data ? (
            <p role="status" className="px-4 py-6 text-[13px] text-fg-muted">
              Carregando perfis…
            </p>
          ) : (
            <div className="max-h-[calc(100vh-15rem)] overflow-auto">
              <table className="w-full min-w-[760px] border-separate border-spacing-0 text-[13px]">
                <caption className="sr-only">Permissões de cada perfil</caption>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="sticky top-0 left-0 z-20 border-b border-line bg-surface px-4 py-2 text-left text-2xs font-medium tracking-wide text-fg-subtle uppercase"
                    >
                      Tela e recurso
                    </th>
                    {list.map((r) => (
                      <th
                        key={r.id}
                        scope="col"
                        className="sticky top-0 z-10 w-28 border-b border-l border-line bg-surface px-2 py-2 align-bottom"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setDrawer({ role: r });
                          }}
                          className="w-full rounded px-1 py-0.5 text-center hover:bg-surface-hover"
                          title={r.description || r.name}
                        >
                          <span className="block text-xs leading-tight font-medium text-fg">
                            {r.name}
                          </span>
                          <span className="mt-0.5 block text-2xs font-normal text-fg-subtle">
                            {kindLabels[r.kind]}
                            {r.kind !== 'integration' &&
                              ` · ${String(r.users)} ${r.users === 1 ? 'usuário' : 'usuários'}`}
                          </span>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {areas.map((g) => (
                    <Fragment key={g.area}>
                      <tr>
                        <th
                          scope="colgroup"
                          colSpan={list.length + 1}
                          className="sticky left-0 border-b border-line bg-canvas px-4 py-1.5 text-left text-2xs font-semibold tracking-wide text-fg-muted uppercase"
                        >
                          {g.area}
                        </th>
                      </tr>
                      {g.items.map((p) => (
                        <tr key={p.key} className="hover:bg-surface-hover/50">
                          <th
                            scope="row"
                            className="sticky left-0 border-b border-line bg-surface px-4 py-2 text-left font-normal text-fg"
                          >
                            {p.label}
                          </th>
                          {list.map((r) => (
                            <td
                              key={r.id}
                              className="border-b border-l border-line px-2 py-2 text-center"
                            >
                              <Cell
                                role={r}
                                perm={p}
                                checked={current(r).includes(p.key)}
                                canManage={canManage}
                                held={can(p.key)}
                                onChange={(on) => {
                                  toggle(r, p.key, on);
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {canManage && changed.length > 0 && (
          <div
            role="region"
            aria-label="Alterações não salvas"
            className="sticky bottom-4 flex items-center gap-3 rounded-lg border border-accent/40 bg-surface-raised px-4 py-3 shadow-lg shadow-black/30"
          >
            <p className="mr-auto text-[13px] text-fg">
              {changed.length === 1
                ? `Alterações no perfil ${changed[0]?.name ?? ''} não salvas.`
                : `Alterações em ${String(changed.length)} perfis não salvas.`}
            </p>
            <Button
              disabled={saving}
              onClick={() => {
                setDraft({});
                setProblem(null);
              }}
            >
              Descartar
            </Button>
            <Button variant="primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Salvando…' : 'Salvar alterações'}
            </Button>
          </div>
        )}
      </div>
      {drawer && roles.data && (
        <ProfileDrawer
          {...(drawer.role ? { role: drawer.role } : {})}
          roles={roles.data}
          canManage={canManage}
          onClose={() => {
            setDrawer(null);
          }}
        />
      )}
    </>
  );
}

function Cell({
  role,
  perm,
  checked,
  canManage,
  held,
  onChange,
}: {
  role: Role;
  perm: PermissionInfo;
  checked: boolean;
  canManage: boolean;
  held: boolean;
  onChange: (on: boolean) => void;
}) {
  const label = `${perm.label} — ${role.name}`;
  if (!perm.kinds.includes(role.kind))
    return (
      <span className="text-fg-subtle" title="Não se aplica a este tipo de perfil">
        <span aria-hidden="true">—</span>
        <span className="sr-only">{label}: não se aplica</span>
      </span>
    );
  if (role.locked)
    return (
      <span title="O administrador geral sempre tem todas as permissões.">
        <Lock aria-hidden="true" className="mx-auto size-3.5 text-fg-subtle" />
        <span className="sr-only">{label}: sempre permitido</span>
      </span>
    );
  const why = !canManage
    ? 'Você não pode alterar as permissões.'
    : !held
      ? 'Você só pode marcar ou desmarcar permissões que você mesmo tem.'
      : undefined;
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={why !== undefined}
      title={why}
      onChange={(e) => {
        onChange(e.target.checked);
      }}
      className="size-4 cursor-pointer accent-[var(--color-accent-strong)] disabled:cursor-not-allowed"
    />
  );
}
